import { Facebook, Instagram, Linkedin, Youtube } from "lucide-react";

const SOCIAL = [
    { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/117352924689063" },
    { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/kitchensafeusa" },
    { icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/kitchensafeusa" },
    { icon: Youtube, label: "YouTube", href: "https://www.youtube.com/kitchensafeusa" },
];

const EXTERNAL = [
    { label: "Houzz", href: "https://www.houzz.com/pro/kitchensafeusa" },
    { label: "Pinterest", href: "https://www.pinterest.com/kitchensafeusa" },
    { label: "X (Twitter)", href: "https://www.x.com/kitchensafeusa" },
    { label: "Yelp", href: "https://www.yelp.com/biz/kitchensafeusa" },
    { label: "Twitch", href: "https://www.twitch.tv/kitchensafeusa" },
];

const PRODUCT_LINKS = [
    { label: "System", href: "#features" },
    { label: "Specifications", href: "#specifications" },
    { label: "Get a Quote", href: "#pricing" },
    { label: "Industries", href: "#industries" },
];

const RESOURCE_LINKS = [
    { label: "Installation Manual", href: "/installation-manual" },
    { label: "FAQ", href: "/#faq" },
    { label: "Privacy", href: "https://kitchensafe.net/privacy-policy" },
    { label: "Terms", href: "https://kitchensafe.net/terms-and-conditions" },
];

function BrandColumn() {
    return (
        <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
                <div className="bg-white rounded-md inline-flex overflow-hidden">
                    <img
                        src="https://customer-assets.emergentagent.com/job_safekitchen-us/artifacts/sd8yhj0j_Content-Creator-Project.jpg"
                        alt="Kitchensafe USA"
                        className="h-14 w-auto block"
                    />
                </div>
                <div className="leading-none border-l border-white/15 pl-3">
                    <div className="font-mono-tech text-[11px] text-[#a1a1aa] uppercase tracking-[0.18em]">
                        USA
                    </div>
                    <div className="font-mono-tech text-[11px] text-[#E53935] uppercase tracking-[0.18em] mt-1">
                        UL300a
                    </div>
                </div>
            </div>
            <p className="text-sm text-[#a1a1aa] leading-relaxed max-w-md mb-6">
                Kitchen exhaust hood fire suppression. UL300a standard certified by Intertek.
                Engineered for the kitchens that need it most.
            </p>
            <div className="flex items-center gap-2">
                {SOCIAL.map((s) => (
                    <a
                        key={s.label}
                        href={s.href}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={s.label}
                        data-testid={`footer-social-${s.label.toLowerCase()}`}
                        className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-[#E53935] hover:bg-[#E53935]/5 hover:text-[#E53935] text-[#a1a1aa] transition-all"
                    >
                        <s.icon className="w-4 h-4" strokeWidth={1.75} />
                    </a>
                ))}
            </div>
        </div>
    );
}

function LinkColumn({ title, links, span = "lg:col-span-2" }) {
    return (
        <div className={span}>
            <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#71717a] mb-4">
                {title}
            </div>
            <ul className="space-y-3 text-sm">
                {links.map((l) => (
                    <li key={l.label}>
                        <a
                            href={l.href}
                            className="text-[#a1a1aa] hover:text-white transition-colors"
                        >
                            {l.label}
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function ContactColumn() {
    return (
        <div className="lg:col-span-3">
            <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#71717a] mb-4">
                Contact Tom
            </div>
            <a
                href="tel:4806945234"
                data-testid="footer-phone"
                className="block font-display font-bold text-2xl text-white hover:text-[#E53935] transition-colors mb-2"
            >
                480-694-5234
            </a>
            <a
                href="mailto:tom@kitchensafe.net"
                data-testid="footer-email"
                className="block text-sm text-[#a1a1aa] hover:text-white transition-colors mb-4"
            >
                tom@kitchensafe.net
            </a>
            <div className="flex flex-wrap gap-x-3 gap-y-2 text-xs">
                {EXTERNAL.map((e) => (
                    <a
                        key={e.label}
                        href={e.href}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#71717a] hover:text-[#E53935] transition-colors"
                    >
                        {e.label}
                    </a>
                ))}
            </div>
        </div>
    );
}

function FooterBottom() {
    return (
        <div className="pt-8 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="font-mono-tech text-[11px] uppercase tracking-[0.18em] text-[#71717a]">
                © {new Date().getFullYear()} Kitchensafe USA — Exhaust hood fire suppression. All
                rights reserved.
            </div>
            <div className="flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-[0.18em] text-[#71717a]">
                <span className="w-1.5 h-1.5 bg-[#22c55e] rounded-full pulse-dot" />
                UL300a · Certified by Intertek
            </div>
        </div>
    );
}

export default function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="relative bg-[#0a0a0b] border-t border-white/10 pt-20 pb-8"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
                <div className="grid lg:grid-cols-12 gap-12 pb-16 border-b border-white/10">
                    <BrandColumn />
                    <LinkColumn title="Product" links={PRODUCT_LINKS} />
                    <LinkColumn title="Resources" links={RESOURCE_LINKS} />
                    <ContactColumn />
                </div>
                <FooterBottom />
            </div>
        </footer>
    );
}
